import React, { useState, useEffect, useRef } from "react";
import apiClient from "utils/apiClient";
import { ACTOR_ID_HEADER_KEY, PM_SERVICE_BASE_URL } from "constants";
import { useAuth } from "contexts/AuthContext";
import { Spin } from "antd";

/* tag component */
const SoftTag = ({ children, onRemove }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: "#eef2ff",
      color: "#4338ca",
      fontSize: 12,
      fontWeight: 500,
      padding: "4px 8px",
      borderRadius: 6,
      lineHeight: "20px",
      fontFamily: "Inter",
    }}
  >
    <span>{children}</span>
    {onRemove && (
      <span
        onClick={onRemove}
        style={{
          cursor: "pointer",
          opacity: 0.6,
          fontSize: 11,
        }}
      >
        ×
      </span>
    )}
  </div>
);

export const SimpleTagManager = ({
  entityType,
  entityId,
  projectId,
  availableTags = [],
  currentTags = [],
  onChange,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef();

  /* api URL for tag updates */
  const apiUrl = (() => {
    switch (entityType) {
      case "task":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks/${entityId}/tags`;
      case "iteration":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/iterations/${entityId}/tags`;
      case "project":
        return `${PM_SERVICE_BASE_URL}/api/v1/projects/${entityId}/tags`;
      default:
        throw new Error("Unsupported entity type");
    }
  })();

  /* filter suggestions based on input and assigned tags */
  useEffect(() => {
    if (!input) return setSuggestions([]);
    const lower = input.toLowerCase();
    setSuggestions(
      availableTags.filter(
        (t) =>
          !currentTags.some((ct) => ct.id === t.id) &&
          t.name.toLowerCase().includes(lower),
      ),
    );
  }, [input, availableTags, currentTags]);

  /* add tag */
  const handleAdd = async (tag) => {
    try {
      setLoading(true);
      await apiClient.post(apiUrl, [tag.id], {
        headers: actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {},
      });
      onChange([...currentTags, tag]);
      setInput("");
      setAdding(false);
    } catch (err) {
      console.error("Add tag failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* remove tag */
  const handleRemove = async (tag) => {
    try {
      setLoading(true);
      await apiClient.delete(apiUrl, {
        params: { tagIds: [tag.id] },
        headers: actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {},
      });
      onChange(currentTags.filter((t) => t.id !== tag.id));
    } catch (err) {
      console.error("Remove tag failed", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
      ref={containerRef}
    >
      {/* existing tags */}
      {currentTags.map((tag) => (
        <SoftTag
          key={tag.id}
          onRemove={!readOnly ? () => handleRemove(tag) : undefined}
        >
          {tag.name}
        </SoftTag>
      ))}

      {/* add new tag */}
      {!readOnly && (
        <>
          {adding ? (
            <div style={{ position: "relative" }}>
              <input
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onBlur={() => setAdding(false)}
                placeholder="Type to search..."
                style={{
                  minWidth: 140,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  outline: "none",
                  fontSize: 12,
                  fontFamily: "Inter",
                  transition: "all 0.2s ease",
                  backgroundColor: "#f9fafb",
                  color: "#111827",
                }}
              />

              {/* oading spinner */}
              {loading && (
                <Spin
                  size="small"
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {/* suggestions list */}
              {suggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 180,
                    overflowY: "auto",
                    zIndex: 999,
                  }}
                >
                  {suggestions.map((tag) => (
                    <div
                      key={tag.id}
                      onMouseDown={() => handleAdd(tag)}
                      style={{
                        padding: "6px 10px",
                        cursor: "pointer",
                        fontSize: 12,
                        fontFamily: "Inter",
                      }}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              onClick={() => setAdding(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: 12,
                fontWeight: 500,
                padding: "4px 8px",
                borderRadius: 6,
                cursor: "pointer",
                border: "1px dashed #d1d5db",
                color: "#6b7280",
                fontFamily: "Inter",
              }}
            >
              {loading ? <Spin size="small" /> : "+ Add tag"}
            </div>
          )}
        </>
      )}
    </div>
  );
};
